import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useTutorAnalytics } from '../../tutor/hooks/useTutorAnalytics';
import type { TutorAnalyticsSummary } from '../../tutor/types/tutorAnalytics';
import { Clock, Activity, Mic, TrendingUp, Calendar, MessageSquare, Sparkles, AlertTriangle, RefreshCw } from 'lucide-react';
import { resolveSpeakingTopicLabel } from '../../tutor/types/tutorAnalytics';
import { useTutorTenantLabels } from '../../modules/tutor/hooks/useTutorTenantLabels';

export type TutorAnalyticsDashboardState = {
  data: TutorAnalyticsSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
};

interface TutorAnalyticsDashboardProps {
  days?: number;
  /** When provided, avoids a duplicate analytics fetch from the parent dashboard. */
  analytics?: TutorAnalyticsDashboardState;
}

export const TutorAnalyticsDashboard: React.FC<TutorAnalyticsDashboardProps> = ({
  days = 30,
  analytics: analyticsProp,
}) => {
  const { t } = useTranslation();
  const internal = useTutorAnalytics({ initialDays: days, skip: Boolean(analyticsProp) });
  const { data, loading, error, refetch } = analyticsProp ?? internal;
  const { labels } = useTutorTenantLabels();

  // Show a warning if there are persistent API issues
  const showApiWarning = error && (error.includes('Server error') || error.includes('Cannot connect'));

  const formatDuration = (minutes: number) => {
    const value = Math.round(minutes * 10) / 10;
    return `${value}m`;
  };

  const formatAvgSessionLength = (seconds: number) => {
    if (!seconds || Number.isNaN(seconds)) return '—';
    if (seconds < 60) return t('dashboard.studentHome.analytics.sec', { n: Math.round(seconds) });
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    if (s === 0) return t('dashboard.studentHome.analytics.min', { n: m });
    return t('dashboard.studentHome.analytics.minSec', { m, s });
  };

  const formatLastActive = (dateString: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return t('dashboard.studentHome.analytics.today');
    if (diffInDays === 1) return t('dashboard.studentHome.analytics.yesterday');
    if (diffInDays < 7) return t('dashboard.studentHome.analytics.daysAgo', { n: diffInDays });
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-app-primary dark:text-pink-400" />
          {labels.activityTitle}
        </h2>
        <p className="text-sm text-app-text-muted dark:text-gray-400 -mt-4">
          {t('dashboard.studentHome.analytics.loadingPeriod', { days })}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mt-8 space-y-6">
        <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-app-primary dark:text-pink-400" />
          {labels.activityTitle}
        </h2>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-4 space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                {error || t('dashboard.studentHome.analytics.noData')}
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-2 text-sm font-semibold text-app-text hover:bg-app-surface-muted dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700 min-h-[44px]"
              >
                <RefreshCw className="w-4 h-4" />
                {t('dashboard.studentHome.retry')}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /** Raw session rows in this period (matches the breakdown card below). */
  const tutorVisitsCount =
    data.session_history.length > 0 ? data.session_history.length : data.sessions_count || 0;

  return (
    <div className="mt-8 space-y-6">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-app-text dark:text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-app-primary dark:text-pink-400" />
            {labels.activityTitle}
          </h2>
          <p className="text-sm text-app-text-muted dark:text-gray-400 mt-1 max-w-2xl">
            {t('dashboard.studentHome.analytics.intro', {
              tutor: labels.moduleTitleAiTutor,
              days,
            })}
          </p>
        </div>
        {showApiWarning && (
          <button
            type="button"
            onClick={refetch}
            className="text-sm text-app-primary hover:text-app-primary dark:text-pink-400 dark:hover:text-pink-300 flex items-center gap-1 shrink-0"
          >
            <RefreshCw className="w-4 h-4" />
            {t('dashboard.studentHome.retry')}
          </button>
        )}
      </div>

      {showApiWarning && (
        <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">
                {t('dashboard.studentHome.analytics.serviceUnavailable')}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-muted dark:text-gray-400 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {t('dashboard.studentHome.analytics.practiceTime')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {data.total_tutor_minutes > 0 ? formatDuration(data.total_tutor_minutes) : '—'}
            </div>
            <p className="text-xs text-app-text-muted dark:text-gray-500 mt-2 leading-snug">
              {t('dashboard.studentHome.analytics.practiceTimeHint', {
                tutor: labels.moduleTitleAiTutor,
              })}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-muted dark:text-gray-400 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              {t('dashboard.studentHome.analytics.tutorVisits')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {tutorVisitsCount > 0 ? tutorVisitsCount : '—'}
            </div>
            <p className="text-xs text-app-text-muted dark:text-gray-500 mt-2 leading-snug">
              {t('dashboard.studentHome.analytics.tutorVisitsHint', {
                tutor: labels.moduleTitleAiTutor,
              })}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-muted dark:text-gray-400 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {t('dashboard.studentHome.analytics.lastPractice')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold text-app-text dark:text-white">
              {formatLastActive(data.last_active_at)}
            </div>
            <p className="text-xs text-app-text-muted dark:text-gray-500 mt-2 leading-snug">
              {t('dashboard.studentHome.analytics.lastPracticeHint')}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-app-text-muted dark:text-gray-400 flex items-center gap-2">
              <Mic className="w-4 h-4" />
              {t('dashboard.studentHome.analytics.speakingLevel')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-app-text dark:text-white">
              {data.latest_speaking_level || '—'}
            </div>
            <p className="text-xs text-app-text-muted dark:text-gray-500 mt-2 leading-snug">
              {t('dashboard.studentHome.analytics.speakingLevelHint')}
            </p>
          </CardContent>
        </Card>
      </div>

      {data.session_history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                {t('dashboard.studentHome.analytics.howYouPracticed')}
              </span>
              <span className="text-sm font-normal text-app-text-muted dark:text-gray-400">
                {data.session_history.length === 1
                  ? t('dashboard.studentHome.analytics.visitInDays', {
                      count: data.session_history.length,
                      days,
                    })
                  : t('dashboard.studentHome.analytics.visitsInDays', {
                      count: data.session_history.length,
                      days,
                    })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold bg-gradient-to-r from-app-primary to-app-primary/75 bg-clip-text text-transparent dark:from-indigo-400 dark:to-pink-400">
                  {data.session_history.filter(s => s.mode === 'VOICE').length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {t('dashboard.studentHome.analytics.withVoice')}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {data.session_history.filter(s => s.mode === 'TEXT').length}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {t('dashboard.studentHome.analytics.withText')}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-app-primary dark:text-purple-400">
                  {formatAvgSessionLength(
                    data.session_history.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) /
                      data.session_history.length
                  )}
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  {t('dashboard.studentHome.analytics.typicalLength')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1">
              <span className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                {t('dashboard.studentHome.analytics.recentVoiceFeedback')}
              </span>
              <span className="text-sm font-normal text-app-text-muted dark:text-gray-400">
                {t('dashboard.studentHome.analytics.recentVoiceHint')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.speaking_sessions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('dashboard.studentHome.analytics.noVoiceScores', {
                  tutor: labels.moduleTitleAiTutor,
                })}
              </p>
            ) : (
              <div className="space-y-3">
                {data.speaking_sessions.slice(0, 2).map((session) => (
                  <div key={session.session_id} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-b-0 last:pb-0">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-app-text dark:text-white">
                          {t('dashboard.studentHome.analytics.voicePractice')}
                        </span>
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/60 text-app-primary dark:text-purple-200 px-2 py-1 rounded border border-purple-200/80 dark:border-purple-700/60">
                          {session.speaking_level}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDuration(session.duration_seconds / 60)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('dashboard.studentHome.analytics.topic')}{' '}
                      </span>
                      <span className="font-medium text-app-text dark:text-gray-200">
                        {resolveSpeakingTopicLabel(session, data.session_history) || '—'}
                      </span>
                    </p>
                    <div className="space-y-3">
                      {[
                        { label: t('dashboard.studentHome.analytics.pronunciation'), value: session.scores.pronunciation, color: 'bg-emerald-500' },
                        { label: t('dashboard.studentHome.analytics.fluency'), value: session.scores.fluency, color: 'bg-gradient-to-r from-indigo-500 to-app-primary/90' },
                        { label: t('dashboard.studentHome.analytics.grammar'), value: session.scores.grammar, color: 'bg-app-primary' },
                        { label: t('dashboard.studentHome.analytics.overall'), value: session.scores.overall, color: 'bg-indigo-500', bold: true },
                      ].map(({ label, value, color, bold }) => (
                        <div key={label} className="flex items-center gap-2">
                          <span className={`text-xs text-gray-500 dark:text-gray-400 w-24 shrink-0 ${bold ? 'font-semibold text-gray-700 dark:text-gray-300' : ''}`}>
                            {label}:
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${color} transition-all duration-500`}
                              style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium w-8 text-right ${bold ? 'font-bold text-app-text dark:text-white' : ''}`}>
                            {value != null ? `${value}%` : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex flex-col gap-1">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {t('dashboard.studentHome.analytics.practiceByDay')}
              </span>
              <span className="text-sm font-normal text-app-text-muted dark:text-gray-400">
                {t('dashboard.studentHome.analytics.practiceByDayHint')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.daily_engagement.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t('dashboard.studentHome.analytics.noDaySummary')}
              </p>
            ) : (
              <div className="space-y-3">
                {data.daily_engagement.slice(0, 5).map((day) => (
                  <div key={day.date} className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {new Date(day.date).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {day.sessions === 1
                          ? t('dashboard.studentHome.analytics.visitThatDay', { n: day.sessions })
                          : t('dashboard.studentHome.analytics.visitsThatDay', { n: day.sessions })}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-app-text dark:text-white">
                        {formatDuration(day.tutor_minutes)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
